import { useEffect, useState } from 'react';
import { Navigate, Route, Routes, useParams } from 'react-router-dom';
import AccountPanel from './components/AccountPanel';
import AppShell from './components/AppShell';
import BillingResultPanel from './components/BillingResultPanel';
import ManagePanel from './components/ManagePanel';
import { buildPlaceholderRun, useRunManager } from './hooks/useRunManager';
import { useAppStore } from './store/useAppStore';

function RunRouteLoader() {
  const { runId = '' } = useParams();
  const { refreshRun, subscribeSSE, cleanup, primeRun, isLiveStatus } = useRunManager();
  const setActiveView = useAppStore((state) => state.setActiveView);
  const [loadState, setLoadState] = useState<'loading' | 'ready' | 'missing'>('loading');

  useEffect(() => {
    if (!runId) {
      setLoadState('missing');
      return;
    }

    setActiveView('workspace');

    const { currentRun, history } = useAppStore.getState();
    const existingRun = currentRun?.runId === runId ? currentRun : null;
    const historyItem = history.find((item) => item.runId === runId) || null;

    if (existingRun) {
      setLoadState('ready');
      if (isLiveStatus(existingRun.status)) {
        subscribeSSE(runId);
        void refreshRun(runId);
      } else {
        cleanup();
      }
      return;
    }

    if (historyItem) {
      primeRun(buildPlaceholderRun({
        runId,
        topic: historyItem.topic,
        status: historyItem.status,
        progress: historyItem.progress,
        message: historyItem.status === 'completed' ? '正在恢复结果视图。' : '正在恢复任务过程。',
        createdAt: historyItem.createdAt,
      }));
      setLoadState('ready');

      const hydrateFromHistory = async () => {
        const record = await refreshRun(runId);
        if (!record) {
          cleanup();
          setLoadState('missing');
          return;
        }

        if (isLiveStatus(record.status)) {
          subscribeSSE(runId);
        } else {
          cleanup();
        }
      };

      void hydrateFromHistory();
      return;
    }

    let cancelled = false;
    setLoadState('loading');

    const load = async () => {
      const record = await refreshRun(runId);
      if (cancelled) return;

      if (!record) {
        cleanup();
        setLoadState('missing');
        return;
      }

      setLoadState('ready');

      if (isLiveStatus(record.status)) {
        subscribeSSE(runId);
      } else {
        cleanup();
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, [runId, refreshRun, subscribeSSE, cleanup, setActiveView, primeRun, isLiveStatus]);

  if (loadState === 'loading') {
    return (
      <div className="route-shell">
        <div className="route-card">
          <p className="route-kicker">Research Task</p>
          <h2 className="route-title">正在恢复任务视图</h2>
          <p className="route-copy">正在载入任务 {runId} 的过程或结果内容，请稍候。</p>
          <div className="route-progress-track">
            <div className="route-progress-fill route-progress-indeterminate" />
          </div>
        </div>
      </div>
    );
  }

  if (loadState === 'missing') {
    return (
      <div className="route-shell">
        <div className="route-card route-card-danger">
          <p className="route-kicker">Task Not Available</p>
          <h2 className="route-title">任务无法打开</h2>
          <p className="route-copy">没有找到该任务的可恢复内容，或后端暂时不可用。请检查 runId 或稍后重试。</p>
          <div className="route-run-id">{runId || 'unknown-run-id'}</div>
        </div>
      </div>
    );
  }

  return <AppShell />;
}

export default function App() {
  return (
    <Routes>
      <Route path="/" element={<AppShell />} />
      <Route path="/account" element={<AccountPanel />} />
      <Route path="/billing/success" element={<BillingResultPanel status="success" />} />
      <Route path="/billing/cancel" element={<BillingResultPanel status="cancel" />} />
      <Route path="/manage" element={<ManagePanel />} />
      <Route path="/runs/:runId" element={<RunRouteLoader />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
